import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { inject, observer } from 'mobx-react';
import styled from '@emotion/styled';
import { clone } from 'mobx-state-tree';
import { css } from '@emotion/core';
import { injectIntl, FormattedMessage } from 'react-intl';
import { isEqual } from 'lodash';
import { getOr } from 'lodash/fp';
import DocumentTitle from 'react-document-title';
import { text1, text2Medium } from '../../../constants/classes';
import ItemImage from '../../common/ItemImage';
import TmModal from '../../common/TmModal';
import ItemInfo from './ItemInfo';
import NavButtonGroup from '../../common/NavButtonGroup';
import Input from '../../common/Input';
import {
  Container,
  FlexWrapper,
  ItemPhotoWrapper,
  ItemDetails,
  ItemName,
  ActionButton,
  ItemInfoContainer,
} from '../../common/SingleItem';
import { title } from '../../../constants';
import { ServiceDown } from '../../common/ServiceDown';

const LAST = 'last';
const FIRST = 'first';
const PREVIOUS = -1;
const NEXT = 1;

const CharactersCount = styled.span`
  font-size: ${({ theme }) => theme.fontSize.medium};
  color: ${({ theme }) => theme.colors.greyInputBorder};
  width: 40%;
  margin-left: 10px;
  display: inline-block;
`;

@injectIntl
@inject('routingStore')
@inject('sessionStore')
@inject('ordersStore')
@inject('listingsPageStore')
@observer
class SingleListingsItem extends Component {
  constructor(props) {
    super(props);
    this.state = {
      singleItem: undefined,
      isTitleLengthShown: false,
      spinnerShown: false,
    };
    this.validateIfNavBtnIsDisabled = this.validateIfNavBtnIsDisabled.bind(this);
    this.validateIfActionBtnsAreDisabled = this.validateIfActionBtnsAreDisabled.bind(this);
    this.handleCancel = this.handleCancel.bind(this);
    this.handleItemUpdate = this.handleItemUpdate.bind(this);
    this.handleItemAsinUpdate = this.handleItemAsinUpdate.bind(this);
    this.showTitleLength = this.showTitleLength.bind(this);
    this.hideTitleLength = this.hideTitleLength.bind(this);
    this.spinnerStack = 0;
  }

  componentDidMount() {
    this.getSingleItem();
  }

  componentDidUpdate(prevState) {
    const {
      params: { code },
    } = this.props;
    if (prevState.params.code !== code) {
      this.getSingleItem();
    }
  }

  componentWillUnmount() {
    const {
      listingsPageStore: { showNavButtons, setShowNavButtons },
    } = this.props;
    if (!showNavButtons) {
      setShowNavButtons(true);
    }
  }

  setSpinner = bool => {
    const { spinnerShown } = this.state;
    const spinnerStackResult = Math.max(bool ? this.spinnerStack + 1 : this.spinnerStack - 1, 0);
    this.spinnerStack = spinnerStackResult;
    if (spinnerShown !== !!spinnerStackResult) {
      this.setState({
        spinnerShown: !!spinnerStackResult,
      });
    }
  };

  setItemToStateAndFetchAll(item) {
    const {
      listingsPageStore: { fetchAllCheckedItems },
    } = this.props;
    this.setState({ singleItem: clone(item) }, () => fetchAllCheckedItems());
  }

  getSingleItem() {
    const {
      listingsPageStore,
      params: { code },
    } = this.props;
    if (this.ifItemFetched()) {
      this.setItemToStateAndFetchAll(listingsPageStore.getSingleItem(code));
    } else {
      this.fetchSingleItem();
    }
  }

  fetchSingleItem() {
    const {
      listingsPageStore: { fetchSingleItemData },
      routingStore: { browserHistory },
      params,
    } = this.props;
    fetchSingleItemData(params.code).then(item => {
      if (item && item.pricingStrategy !== null) {
        this.setItemToStateAndFetchAll(item);
      } else {
        browserHistory.push('/listings');
      }
    });
  }

  ifItemFetched() {
    const {
      listingsPageStore: { getSingleItem },
      params: { code },
    } = this.props;
    return getSingleItem(code) !== undefined;
  }

  validateIfNavBtnIsDisabled(place) {
    const {
      params: { code },
      listingsPageStore: { checkedOrdersArray },
    } = this.props;
    const index = place === LAST ? checkedOrdersArray.length - 1 : 0;
    return checkedOrdersArray[index] === code || !checkedOrdersArray.length;
  }

  validateIfActionBtnsAreDisabled() {
    const {
      listingsPageStore: { getSingleItem },
      params,
    } = this.props;
    const { singleItem } = this.state;
    const sourceItem = getSingleItem(params.code);
    return isEqual(singleItem, sourceItem);
  }

  handleCancel() {
    const { listingsPageStore, params } = this.props;
    this.setState({
      singleItem: clone(listingsPageStore.getSingleItem(params.code)),
    });
  }

  handleItemUpdate() {
    const {
      listingsPageStore: { updateSingleItem },
    } = this.props;
    const { singleItem } = this.state;
    this.setState({ spinnerShown: true });
    updateSingleItem(singleItem).then(resp => {
      this.setState({ singleItem: clone(resp), spinnerShown: false });
      this.child.refreshAsinActive();
    });
  }

  handleItemAsinUpdate() {
    const {
      listingsPageStore: { updateAsinSingleItem },
    } = this.props;
    const { singleItem } = this.state;
    this.setState({ spinnerShown: true });
    updateAsinSingleItem(singleItem).then(resp => {
      this.setState({ singleItem: clone(resp), spinnerShown: false });
      this.child.refreshAsinActive();
    });
  }

  showTitleLength() {
    this.setState({ isTitleLengthShown: true });
  }

  hideTitleLength() {
    this.setState({ isTitleLengthShown: false });
  }

  addSpecificAndRefreshItem=(payload)=>{
    const {
      listingsPageStore: { addItemSpecific },
    } = this.props;

    return addItemSpecific(payload).then(()=>{
      this.getSingleItem();
    })
  }

  deleteItemSpecificAndRefreshItem=(itemSpecificId,itemId)=>{
    const {
      listingsPageStore: { deleteItemSpecific },
    } = this.props;

    return deleteItemSpecific(itemSpecificId,itemId).then(()=>{
      this.getSingleItem();
    })
  }

  render() {
    const {
      params,
      listingsPageStore,
      intl,
      sessionStore: { isNonApi },
    } = this.props;
    const {
      switchItem,
      handleReturn,
      getSourceName,
      getTemplateName,
      templateList,
      currentLanguage,
      showNavButtons,
      isUp,
      isUpdated,
      setIsUpdated,
      updateSpecific,
      isSpecificUpdating
    } = listingsPageStore;
    const { singleItem, isTitleLengthShown, spinnerShown } = this.state;
    const { code } = params;
    const noItem = isUp ? <div className="loader" /> : <ServiceDown />;

    return (
      <DocumentTitle title={singleItem ? `${title} | ${singleItem.title}` : title}>
        <Container>
          {singleItem ? (
            <Fragment>
              <TmModal
                type="info"
                title={intl.formatMessage({ id: 'common.pending' })}
                visible={isUpdated && isNonApi}
                secondaryActionBtn={{
                  text: `${intl.formatMessage({ id: 'common.continue' })}`,
                  clickHandler: () => {
                    setIsUpdated(false);
                  },
                }}
              >
                <h3>{intl.formatMessage({ id: 'listing.nonApiUpdateMessage' })}</h3>
              </TmModal>
              <h1
                css={css`
                  ${text1};
                `}
              >
                <FormattedMessage id="common.listings" />
              </h1>
              {showNavButtons && (
                <NavButtonGroup
                  disabledPrevious={this.validateIfNavBtnIsDisabled(FIRST)}
                  previousAction={() => switchItem(code, PREVIOUS)}
                  returnAction={handleReturn}
                  disabledNext={this.validateIfNavBtnIsDisabled(LAST)}
                  nextAction={() => switchItem(code, NEXT)}
                  css={css`
                    margin-bottom: 14px;
                  `}
                />
              )}
              <FlexWrapper>
                <ItemPhotoWrapper>
                  {singleItem.pictures.length !== 0 ? (
                    <ItemImage
                      mainImage={singleItem.pictures[0].url}
                      pictures={singleItem.pictures}
                    />
                  ) : (
                    <div style={{ width: '387px' }} />
                  )}
                </ItemPhotoWrapper>
                <ItemDetails>
                  <ItemName
                    inline
                    css={css`
                      padding: 10px 0;
                    `}
                  >
                    <Input
                      value={singleItem.title}
                      maxLength={80}
                      onFocus={this.showTitleLength}
                      onBlur={this.hideTitleLength}
                      onChange={e => singleItem.setTitle(e.target.value)}
                      css={css`
                        ${text2Medium};
                        height: 30px;
                        width: 70%;
                        border-radius: 20px;
                      `}
                    />

                    {isTitleLengthShown && (
                      <CharactersCount>
                        ({singleItem.title.length}/80 <FormattedMessage id="common.characters" />)
                      </CharactersCount>
                    )}
                  </ItemName>
                  <FlexWrapper>
                    <ItemInfoContainer>
                      <ItemInfo
                        onRef={ref => {
                          this.child = ref;
                        }}
                        data={{
                          ...singleItem,
                          marketplaceName: getSourceName(singleItem.sourceMarketplaceId),
                          getTemplateName,
                          templateList,
                          language: currentLanguage,
                          addLabel: singleItem.addLabel,
                          removeLabel: singleItem.removeLabel,
                          setAsin: singleItem.setAsin,
                          setStatus: singleItem.setStatus,
                          setDestinationPrice: singleItem.setDestinationPrice,
                          setBreakEven: singleItem.setBreakEven,
                          setProfit: singleItem.setProfit,
                          setPercentProfit: singleItem.setPercentProfit,
                          setUnitProfit: singleItem.setUnitProfit,
                          setQuantity: singleItem.setQuantity,
                          setTemplate: singleItem.setTemplate,
                          setStrategyPercents: singleItem.setStrategyPercents,
                          setMonitoringStatus: singleItem.setMonitoringStatus,
                          setOriginalCarrier: singleItem.setOriginalCarrier,
                          setOriginalTrackingNumber: singleItem.setOriginalTrackingNumber,
                          multiVariation: getOr(false, 'variations.length', singleItem),
                          isNonApi,
                          updateSpecific,
                          addItemSpecific:this.addSpecificAndRefreshItem,
                          deleteItemSpecific:this.deleteItemSpecificAndRefreshItem,
                          setSpinner:this.setSpinner,
                          isSpecificUpdating
                        }}
                        cloneIsEqual={this.validateIfActionBtnsAreDisabled()}
                        handleItemAsinUpdate={this.handleItemAsinUpdate}
                      />
                      <div>
                        <ActionButton
                          htmlType="submit"
                          clickHandler={this.handleItemUpdate}
                          disabled={this.validateIfActionBtnsAreDisabled()}
                        >
                          <FormattedMessage id="common.save" />
                        </ActionButton>
                        <ActionButton
                          type="secondary"
                          clickHandler={this.handleCancel}
                          disabled={this.validateIfActionBtnsAreDisabled()}
                        >
                          <FormattedMessage id="common.cancel" />
                        </ActionButton>
                      </div>
                    </ItemInfoContainer>
                  </FlexWrapper>
                </ItemDetails>
              </FlexWrapper>
              {spinnerShown && <div className="loader" />}
            </Fragment>
          ) : (
            noItem
          )}
        </Container>
      </DocumentTitle>
    );
  }
}

SingleListingsItem.propTypes = {
  params: PropTypes.object.isRequired,
  listingsPageStore: PropTypes.object,
  sessionStore: PropTypes.object,
  intl: PropTypes.object,
  routingStore: PropTypes.object,
};

export default SingleListingsItem;
